import { http } from "msw";
import type { Community, Creator, CreatorSummary, Page, Post } from "../../api/types";
import { getCollection } from "../db";
import { paginateCursor, search } from "../engine";
import * as viewerState from "../viewer-state";
import { allPosts, decorateCommunity, decorateCreatorSummary, decoratePost, mockJson, notFound, runMutation, toCreatorSummary } from "./shared";

/** Small deterministic string hash used only to assign a stable, believable member roster per community. */
function hash(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) h = (Math.imul(31, h) + input.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export const communitiesHandlers = [
  http.get("/api/communities", async ({ request }) => {
    const url = new URL(request.url);
    const q = url.searchParams.get("q") ?? "";
    const category = url.searchParams.get("category");
    let communities = await getCollection<Community>("communities");
    communities = search(communities, q, [(c) => c.name, (c) => c.description]);
    if (category) communities = communities.filter((c) => c.category === category);
    return mockJson(communities.map(decorateCommunity));
  }),

  http.get("/api/communities/:slug", async ({ params }) => {
    const communities = await getCollection<Community>("communities");
    const community = communities.find((c) => c.slug === params.slug);
    if (!community) return notFound("Community not found");
    return mockJson(decorateCommunity(community));
  }),

  http.get("/api/communities/:slug/posts", async ({ request, params }) => {
    const url = new URL(request.url);
    const cursor = url.searchParams.get("cursor");
    const limit = Number(url.searchParams.get("limit") ?? 20) || 20;
    const posts = (await allPosts()).filter((p: Post) => p.community?.slug === params.slug);
    const page = paginateCursor(posts, cursor, limit);
    const body: Page<Post> = { items: page.items.map(decoratePost), nextCursor: page.nextCursor };
    return mockJson(body);
  }),

  http.get("/api/communities/:slug/members", async ({ request, params }) => {
    const url = new URL(request.url);
    const cursor = url.searchParams.get("cursor");
    const limit = Number(url.searchParams.get("limit") ?? 30) || 30;
    const slug = String(params.slug);
    const creators = await getCollection<Creator>("creators");
    const members = creators.filter((c) => hash(`${c.id}:${slug}`) % 5 === 0);
    const page = paginateCursor(members, cursor, limit);
    const summaries: CreatorSummary[] = page.items.map((c) => decorateCreatorSummary(toCreatorSummary(c)));
    const body: Page<CreatorSummary> = { items: summaries, nextCursor: page.nextCursor };
    return mockJson(body);
  }),

  http.post("/api/communities/:slug/membership", async ({ params }) => {
    const slug = String(params.slug);
    const communities = await getCollection<Community>("communities");
    const community = communities.find((c) => c.slug === slug);
    if (!community) return notFound("Community not found");
    return runMutation(() => {
      viewerState.setCommunityJoined(slug, true);
      const decorated = decorateCommunity(community);
      return { joined: decorated.joined, members: decorated.members };
    });
  }),
  http.delete("/api/communities/:slug/membership", async ({ params }) => {
    const slug = String(params.slug);
    const communities = await getCollection<Community>("communities");
    const community = communities.find((c) => c.slug === slug);
    if (!community) return notFound("Community not found");
    return runMutation(() => {
      viewerState.setCommunityJoined(slug, false);
      const decorated = decorateCommunity(community);
      return { joined: decorated.joined, members: decorated.members };
    });
  }),
];
